import { Box, Chip, Grid, Paper, Typography } from "@mui/material";
import { PageHeader } from "../components/PageHeader";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { UptimeBarChart } from "../components/UptimeBarChart";
import { useStatusPage } from "../hooks";
import {
  allianceUptimeIndicatorBg,
  uptimeStatusLabel,
} from "../utils/uptimeStatus";

function formatOverall(uptime: number | null | undefined): string {
  if (uptime == null) return "-";
  return `${uptime.toFixed(2)}%`;
}

function formatLatency(ms: number | null | undefined): string {
  if (ms == null) return "-";
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  return `${ms} ms`;
}

function formatUpdated(value: string | null): string {
  if (!value) return "Još nema podataka";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("hr-HR");
}

export function StatusPage() {
  const { apiSeries, allianceList, loading, error } = useStatusPage();
  const overall = apiSeries?.overallUptime ?? null;
  const status = uptimeStatusLabel(overall);

  return (
    <Box>
      <PageHeader
        title="Status"
        subtitle="Praćenje dostupnosti REST API instance i samih web stranica saveza tijekom zadnjih 14 dana. Dostupnost je praćena adekvatnom bojom i kratkim opisom/statusom."
      />

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading && !apiSeries ? (
        <LoadingSpinner py={8} />
      ) : (
        <>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 4,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              gap: 2,
              alignItems: { xs: "flex-start", sm: "center" },
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ mb: 0.5 }}>
                {status.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Zadnje ažuriranje:{" "}
                {formatUpdated(apiSeries?.lastUpdated ?? null)}
              </Typography>
            </Box>
            <Chip
              color={status.color}
              label={`Dostupnost: ${formatOverall(overall)}`}
              sx={{ fontFamily: "monospace", fontWeight: 600 }}
            />
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="h6">Savezi API dostupnost</Typography>
            </Box>
            {apiSeries && <UptimeBarChart daily={apiSeries.daily} />}
          </Paper>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">Dostupnosti saveza</Typography>
            {allianceList && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {allianceList.totalAlliances} saveza u registru ·{" "}
                {allianceList.monitorableAlliances} s poveznicom za praćenje
              </Typography>
            )}
          </Box>
          <Grid container spacing={2}>
            {(allianceList?.alliances ?? []).map((alliance) => (
              <Grid key={alliance.allianceId} size={{ xs: 12, md: 6, xl: 4 }}>
                <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: allianceUptimeIndicatorBg(
                          alliance.overallUptime,
                          alliance.hasMonitorableLink,
                        ),
                      }}
                    />
                    <Typography variant="subtitle2">{alliance.name}</Typography>
                  </Box>
                  {alliance.hasMonitorableLink ? (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {alliance.sport} ·{" "}
                        {formatOverall(alliance.overallUptime)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        Latencija: {formatLatency(alliance.averageLatencyMs)}
                      </Typography>
                      <UptimeBarChart
                        daily={alliance.daily}
                        height={56}
                        compact
                      />
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Nema poveznice za praćenje dostupnosti.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>

          {allianceList?.alliances.length === 0 && (
            <Typography color="text.secondary">
              Nema saveza u registru.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
